import React from 'react';

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error,
  helpText,
  rows = 4,
  maxLength,
  showWordCount = false,
  className = '',
  resize = 'vertical',
  ...props
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  const fieldId = `textarea-${name}`;
  const hasError = Boolean(error);
  const currentLength = (value || '').length;
  
  const baseTextareaClasses = `
    w-full px-3 py-2 border rounded-lg shadow-sm transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    disabled:bg-gray-100 disabled:cursor-not-allowed
    ${resize === 'none' ? 'resize-none' : resize === 'vertical' ? 'resize-y' : 'resize'}
    ${hasError 
      ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300 bg-white hover:border-gray-400'
    }
  `;

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={fieldId}
          className={`block text-sm font-medium ${hasError ? 'text-red-700' : 'text-gray-700'}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Textarea Field */}
      <div className="relative">
        <textarea
          id={fieldId}
          name={name}
          value={value || ''}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          className={baseTextareaClasses.trim()}
          {...props}
        />
        
        {/* Word Count - positioned at bottom right of textarea */}
        {showWordCount && maxLength && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-1 rounded">
            {currentLength}/{maxLength}
          </div>
        )}
      </div>

      {/* Word Count and Help Text Row */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Help Text */}
          {helpText && !hasError && (
            <p className="text-sm text-gray-500">{helpText}</p>
          )}

          {/* Error Message */}
          {hasError && (
            <p className="text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>

        {/* Word Count (alternative position) */}
        {showWordCount && maxLength && !hasError && !helpText && (
          <div className="text-sm text-gray-400 ml-2">
            {currentLength}/{maxLength}
          </div>
        )}
      </div>

      {/* Word count warning */}
      {maxLength && currentLength > maxLength * 0.9 && (
        <div className={`text-sm flex items-center ${
          currentLength >= maxLength ? 'text-red-600' : 'text-amber-600'
        }`}>
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {currentLength >= maxLength 
            ? 'Maximum length reached' 
            : `Approaching maximum length (${maxLength - currentLength} characters remaining)`
          }
        </div>
      )}
    </div>
  );
};

export default TextAreaField;
